import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LogOut, ChevronDown, Eye, EyeOff, Lock } from "lucide-react";
import { logout, getCurrentUser } from "../lib/auth.js";
import { apiFetch } from "../lib/api.js";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
// Struktura menu žije v lib/menu.js, ať ji stejně vidí i spodní lišta na telefonu.
import { navItems, polozkyProRoli } from "../lib/menu.js";
import { useMenuOdznaky } from "../lib/menuOdznaky.js";


export default function Sidebar({ open, setOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();
  // Status "Ukončeno" (16.7.2026): trenér v ukončené spolupráci má v menu
  // UZ JEN Fakturaci (trainers.status z HR → Lidé, viz TrenerLayout).
  const [trenerUkoncen, setTrenerUkoncen] = useState(false);
  // 2.9.2026: kalendář rezervací se trenérům zapíná postupně (REZERVACE_KALENDAR_TRENERI);
  // owner (i v náhledu) ho vidí vždy. 14.9.2026 (fáze 6): trenér startuje
  // se schovaným kalendářem, ať se položka neukáže před odpovědí API.
  const [kalendarZapnuty, setKalendarZapnuty] = useState(() => (getCurrentUser()?.role || "") !== "trener");
  useEffect(() => {
    const u = getCurrentUser();
    if ((u?.role || "") !== "trener") { setTrenerUkoncen(false); setKalendarZapnuty(true); return; }
    // Nahled ownera (17.7.2026): role je simulovane "trener", skutecny status
    // se bere pro trenera zvoleneho v impersonaci (?jmeno=...), aby nahled
    // ukazoval menu presne jako ukonceny trener — jen Fakturaci.
    const realOwner = (u?._realRole || "") === "owner";
    let url = "/api/my-trainer";
    if (realOwner) {
      let va = "";
      try { va = localStorage.getItem("fw40_view_as_trener") || ""; } catch { /* ignoruj */ }
      if (!va) { setTrenerUkoncen(false); return; }
      url = "/api/my-trainer?jmeno=" + encodeURIComponent(va);
    }
    let zij = true;
    apiFetch(url).then((r) => r.json()).then((j) => {
      if (!zij) return;
      setTrenerUkoncen(/ukon/i.test(String(j?.trainer?.status || "")) || j?.trainer?.active === false);
      // 10.9.2026: náhled ownera za trenéra ukazuje kalendář jen tehdy, když ho
      // ten trenér opravdu má. Dřív tu bylo `realOwner ||`, takže se v náhledu
      // ukazoval vždycky a vypadalo to, že ho vidí celý tým.
      setKalendarZapnuty(j?.rezervace_kalendar === true);
    }).catch(() => {});
    return () => { zij = false; };
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps


  // Stav pro rozbalené skupiny v menu (klíč = label skupiny).
  // Persist do localStorage, aby po refreshi zůstal sidebar v stejném stavu jak
  // ho user nechal (jinak se collapsoval všechny dropdowny → frustrace).
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Manažer: dropdowny defaultně zavřené (otevře se jen sekce, do které vejde).
    if (user?.role === "manager") return {};
    try {
      const saved = localStorage.getItem("fw40_sidebar_expanded");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  // Per change uložit do localStorage
  useEffect(() => {
    try { localStorage.setItem("fw40_sidebar_expanded", JSON.stringify(expandedGroups)); }
    catch { /* private mode swallow */ }
  }, [expandedGroups]);

  // 7.9.2026: trenér vidí položku Bootcampy jen tehdy, když mu je nějaký běh
  // přiřazený. Jeden lehký dotaz při načtení, jen pro roli trenér.
  const [maBootcamp, setMaBootcamp] = useState(false);
  useEffect(() => {
    if ((user?.role || "") !== "trener") { setMaBootcamp(false); return; }
    let zrusen = false;
    // 1.10.2026: v náhledu ownera se ptáme za zvoleného trenéra (?jmeno=), jinak
    // server odpoví za ownera (je manažer, má vždy ano) a v náhledu svítily
    // Bootcampy i u trenérky bez běhu.
    let jakoTrener = "";
    try { jakoTrener = localStorage.getItem("fw40_view_as_trener") || ""; } catch { /* ignoruj */ }
    const urlBc = "/api/bootcampy?mujBeh=1" + (jakoTrener ? "&jmeno=" + encodeURIComponent(jakoTrener) : "");
    apiFetch(urlBc)
      .then((r) => r.json())
      .then((j) => { if (!zrusen) setMaBootcamp(!!(j && j.maBeh)); })
      .catch(() => { /* menu bez položky je bezpečnější než rozbité menu */ });
    return () => { zrusen = true; };
  }, [user?.role]);

  // Odznaky (počty) u položek menu. 1. 11. 2026: načítání se přesunulo do
  // lib/menuOdznaky.js, ať stejná čísla bez dalších dotazů vidí i spodní lišta na telefonu.
  const badges = useMenuOdznaky();

  // Pokud je aktuální cesta uvnitř nějaké skupiny, automaticky ji rozbal
  useEffect(() => {
    const newState = { ...expandedGroups };
    let changed = false;
    navItems.forEach((item) => {
      if (item.children) {
        const isInside = item.children.some((c) => location.pathname.startsWith(c.to));
        if (isInside && !newState[item.label]) {
          newState[item.label] = true;
          changed = true;
        }
      }
    });
    if (changed) setExpandedGroups(newState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (label) => {
    setExpandedGroups((s) => ({ ...s, [label]: !s[label] }));
  };

  const handleLogout = async () => {
    await logout(); // počká na auth-logout response (cookie clear)
    navigate("/login");
  };

  return (
    <>
      {/* 2.9.2026: zásuvka na telefonu se ruší, nahradila ji spodní lišta
          (layout/SpodniLista.jsx). Panel je od téhle chvíle jen na počítači,
          na `lg` a výš, a chová se přesně jako dřív. */}
      <aside
        className="sticky top-0 z-40 hidden h-screen w-64 shrink-0 flex-col bg-gradient-to-b from-brand-950 to-brand-900 text-white lg:flex"
      >
        <div className="p-4 sm:p-5 border-b border-white/10 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-10 h-10 sm:w-9 sm:h-9 rounded-xl bg-brand flex items-center justify-center flex-shrink-0">
              <img src="/ikona-bila.svg" alt="" className="w-[62%] h-[62%] object-contain" />
            </div>
            <div className="min-w-0">
              <div className="font-bold truncate text-base sm:text-sm">FitWay40 admin</div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <DemoToggleIcon />
          </div>
        </div>

        <nav className="flex-1 p-3 overflow-y-auto">
          {polozkyProRoli(user?.role || "", {
            realRole: user?._realRole || "",
            trenerUkoncen,
            kalendarZapnuty,
            maBootcamp,
          })
            .map((item) => {
            const Icon = item.icon;

            // Skupina s podpoložkami (Reporting)
            if (item.children) {
              // Filtruj i podpoložky per role
              const visibleChildren = item.children.filter((c) => {
                if (c.roles && user?.role && !c.roles.includes(user.role)) return false;
                // HR je pro manažera a CSM otevřené jen kvůli podpisu do Gmailu
                // (19.8.2026). Zbytek HR mají jinde nebo tam pro ně není co dělat.
                // Manager ma v HR i Skoleni (21.8.2026 - zadava testy smluv trenerum), CSM jen podpis.
                // 15.9.2026: + Potencialni treneri. Stejny seznam musi byt i v lib/menu.js
                // (polozkyProRoli), tady se filtr opakuje a bez doplneni polozku schoval.
                // 20. 10. 2026: sjednoceno s lib/menu.js (Moje školení, Kniha úrazů tu chyběly a schovávaly se).
                if (item.label === "HR" && user?.role === "manager" && !["/hr/podpis", "/hr/skoleni", "/moje-skoleni", "/hr/kandidati", "/hr/absence", "/kniha-urazu"].includes(c.to)) return false;
                // CSM ma v HR podpis a Bonusy a srazky (31.8.2026 - zadava bonusy
                // trenerum; schvalit ani vyplatit je nemuze, to zustava ownerovi).
                if (item.label === "HR" && user?.role === "csm" && !["/hr/podpis", "/hr/bonusy-srazky", "/hr/absence", "/kniha-urazu", "/moje-skoleni"].includes(c.to)) return false;
                return true;
              });
              if (visibleChildren.length === 0) return null;
              item = { ...item, children: visibleChildren };
              const expanded = !!expandedGroups[item.label];
              const childActive = item.children.some((c) => location.pathname.startsWith(c.to));
              return (
                <div key={item.label} className="mb-1">
                  <button
                    onClick={() => toggleGroup(item.label)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                      childActive
                        ? "bg-white/10 text-white"
                        : "text-white/70 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="truncate flex-1 text-left">{item.label}</span>
                    <ChevronDown
                      className={`w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0 transition-transform ${
                        expanded ? "" : "-rotate-90"
                      }`}
                    />
                  </button>
                  {expanded && (
                    <div className="ml-3 pl-2 border-l border-white/10 mt-1 space-y-0.5">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        // Zamčená položka — zašedlá, se zámkem, neklikací (dočasně nedostupné).
                        if (child.locked) {
                          return (
                            <div
                              key={child.to}
                              title="Zamčeno, zatím nedostupné"
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-white/40 cursor-not-allowed select-none"
                            >
                              <ChildIcon className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0 opacity-60" />
                              <span className="truncate flex-1">{child.label}</span>
                              <Lock className="w-4 h-4 sm:w-3.5 sm:h-3.5 flex-shrink-0 opacity-70" />
                            </div>
                          );
                        }
                        // Pokud má child sourozence s prefixem (např. /clients vs /clients/log-expired),
                        // tento child musí být active jen na exact match (end=true), jinak by oba svítily.
                        const hasSiblingSubpath = item.children.some(
                          (c) => c.to !== child.to && c.to.startsWith(child.to + "/")
                        );
                        const badgeCount = child.badgeKey ? badges[child.badgeKey] || 0 : 0;
                        return (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            end={hasSiblingSubpath}
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                              `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                                isActive
                                  ? "bg-brand text-white"
                                  : "text-white/80 hover:bg-white/10 hover:text-white"
                              }`
                            }
                          >
                            <ChildIcon className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span className="truncate flex-1">{child.label}</span>
                            {child.wip && (
                              <span
                                className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-brand-gold text-brand-dark shrink-0 inline-flex items-center gap-1"
                                title="Pracujeme na tom, brzy spustíme"
                              >
                                <span className="animate-pulse">●</span> Brzy
                              </span>
                            )}
                            {badgeCount > 0 && (
                              <span
                                className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded-xl bg-brand-gold text-brand-dark shrink-0"
                                title={child.badgeKey === "absence_pending" ? `${badgeCount} absencí čeká na schválení`
                                  : child.badgeKey === "upominky_at_limit" ? `${badgeCount} osob s 3+ upomínkami tento měsíc` : String(badgeCount)}
                              >
                                {badgeCount}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Obyčejná položka
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-brand text-white"
                      : "text-white/80 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate flex-1">{item.label}</span>
                {item.badgeKey && (badges[item.badgeKey] || 0) > 0 && (
                  <span
                    className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded-xl bg-brand-gold text-brand-dark shrink-0"
                    title={item.badgeKey === "sales_dnes" ? `${badges[item.badgeKey]} lidí dnes čeká na zavolání` : item.badgeKey === "hodnoceni_vyzivy" ? `${badges[item.badgeKey]} klientek po nutriční konzultaci čeká na zavolání` : String(badges[item.badgeKey])}
                  >
                    {badges[item.badgeKey]}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-1 text-xs text-white/70">
            <div className="font-medium truncate text-xs">
              {user?.name || "-"}
            </div>
            <div>{user?.role || "-"}</div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-rose-200 bg-rose-900/30 hover:bg-rose-900/60 hover:text-white border border-rose-900/40 transition-colors min-h-[44px] sm:min-h-0"
          >
            <LogOut className="w-5 h-5 sm:w-4 sm:h-4" />
            Odhlásit
          </button>
        </div>
      </aside>
    </>
  );
}

// Demo režim ikona — jen oko, bez textu. Klik přepne globální stav (Context).
// Aktivní = violet pozadí + Eye, neaktivní = transparentní + EyeOff.
function DemoToggleIcon() {
  const { blurred, toggleBlurred } = useDemoMode();
  return (
    <button
      onClick={toggleBlurred}
      className={`p-1.5 rounded-xl transition-colors ${
        blurred
          ? "bg-brand text-white hover:bg-brand-deep ring-2 ring-brand-300/40"
          : "text-white/70 hover:bg-white/10 hover:text-white"
      }`}
      title={blurred ? "Demo režim ZAPNUTÝ, citlivá data jsou rozmazaná. Klikni pro vypnutí." : "Zapnout demo režim (rozmaže čísla a jména klientů pro prezentaci)"}
      aria-label={blurred ? "Vypnout demo režim" : "Zapnout demo režim"}
    >
      {blurred ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
    </button>
  );
}
